import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trash, ArrowRight } from "@phosphor-icons/react";
import { useI18n } from "../lib/i18n";
import { listCarousels, deleteCarousel } from "../lib/api";
import { toast } from "sonner";

export default function History() {
  const { t } = useI18n();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const refresh = () => {
    setLoading(true);
    listCarousels().then(setItems).finally(() => setLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const remove = async (id) => {
    if (!window.confirm(t("confirm_delete"))) return;
    try {
      await deleteCarousel(id);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success(t("copied"));
    } catch (e) {
      toast.error("Error");
    }
  };

  return (
    <div className="px-6 md:px-10 pt-12 pb-24 max-w-6xl mx-auto" data-testid="history-page">
      <div className="kicker-label mb-3">— {t("nav_history")}</div>
      <h1 className="font-heading font-semibold text-[40px] md:text-[56px] leading-[0.95] tracking-tight text-ink mb-3">{t("history_title")}</h1>
      <p className="text-ink-soft text-lg mb-10">{t("history_sub")}</p>

      {loading ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="aspect-square rounded-xl bg-cream-warm animate-pulse" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-line p-12 text-center" data-testid="history-empty">
          <div className="font-heading text-xl text-ink mb-2">{t("empty_history_title")}</div>
          <div className="text-ink-soft mb-6">{t("empty_history_sub")}</div>
          <Link to="/create" className="inline-flex items-center gap-2 bg-forest text-cream rounded-full px-6 py-3 text-sm font-medium hover:bg-forest-dark transition">
            {t("cta_create")} <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="history-list">
          {items.map((c) => (
            <div key={c.id} className="group relative rounded-2xl border border-line bg-white canvas-shadow overflow-hidden hover:-translate-y-1 transition" data-testid={`history-item-${c.id}`}>
              <Link to={`/editor/${c.id}`} className="block aspect-square" data-testid={`history-open-${c.id}`}>
                {c.slides?.[0] ? <MiniSlide slide={c.slides[0]} /> : <div className="w-full h-full bg-cream-warm" />}
              </Link>
              <div className="p-4 border-t border-line">
                <div className="text-[10px] tracking-[0.14em] uppercase text-ink-soft mb-1">
                  {c.language === "el" ? "EL" : "EN"} · {c.slides?.length || 0} slides
                </div>
                <div className="font-heading text-ink text-sm font-medium truncate">{c.title}</div>
                <div className="flex items-center justify-between mt-3">
                  <Link to={`/editor/${c.id}`} className="text-xs text-forest hover:text-forest-dark font-medium">
                    {t("open")} →
                  </Link>
                  <button onClick={() => remove(c.id)} className="text-ink-soft hover:text-destructive" data-testid={`history-delete-${c.id}`}>
                    <Trash size={14} weight="duotone" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MiniSlide({ slide }) {
  return (
    <div className="w-full h-full flex items-center justify-center p-5 text-center"
      style={{ background: slide.bg_color || "#F9F8F6", color: slide.text_color || "#15251E" }}>
      <div className="font-heading font-semibold text-[16px] leading-[1.1] line-clamp-5">
        {slide.title || slide.kicker || "—"}
      </div>
    </div>
  );
}
